import { useState } from 'react';
import type { FormEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Pencil, Power } from 'lucide-react';
import { EvacuationPoint } from '@cloudsign/shared';
import { api } from '../../lib/api';
import { t } from '../../lib/i18n';
import { Badge, Button, Card, EmptyState, Spinner, TextField } from '../../components/ui';
import { ErrorLine, ErrorPanel } from '../../components/ErrorPanel';
import { SortableList } from '../../components/SortableList';
import { evacKey, pointsKey, usePoints } from './api';

interface Draft {
  name: string;
  directions: string;
  lat: string;
  lng: string;
}

const BLANK: Draft = { name: '', directions: '', lat: '', lng: '' };

/** Returns an error message key for the position fields, or null when they are fine. */
export function positionProblem(lat: string, lng: string): string | null {
  const a = lat.trim();
  const b = lng.trim();
  if (a === '' && b === '') return null;
  if (a === '' || b === '') return 'evac.points.positionBoth';
  const la = Number(a);
  const lo = Number(b);
  if (!Number.isFinite(la) || !Number.isFinite(lo) || la < -90 || la > 90 || lo < -180 || lo > 180) return 'evac.points.positionRange';
  return null;
}

function PointForm({ initial, pending, error, onSave, onCancel }: { initial: Draft; pending: boolean; error: unknown; onSave: (d: Draft) => void; onCancel: () => void }) {
  const [d, setD] = useState(initial);
  const problem = positionProblem(d.lat, d.lng);
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (d.name.trim() !== '' && !problem) onSave(d);
  };
  return (
    <form onSubmit={submit} className="space-y-3 rounded-lg border border-slate-200 bg-slate-50 p-4">
      <ErrorLine error={error} />
      <TextField label={t('evac.points.name')} required maxLength={80} value={d.name} onChange={(e) => setD({ ...d, name: e.target.value })} />
      <div>
        <label htmlFor="evac-point-directions" className="mb-1 block text-sm font-medium text-slate-800">
          {t('evac.points.directions')}
        </label>
        <textarea
          id="evac-point-directions"
          rows={2}
          maxLength={500}
          value={d.directions}
          onChange={(e) => setD({ ...d, directions: e.target.value })}
          className="block w-full rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm"
        />
        <p className="mt-1 text-xs text-slate-600">{t('evac.points.directionsHint')}</p>
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <TextField label={t('evac.points.lat')} inputMode="decimal" value={d.lat} onChange={(e) => setD({ ...d, lat: e.target.value })} />
        <TextField
          label={t('evac.points.lng')}
          inputMode="decimal"
          value={d.lng}
          onChange={(e) => setD({ ...d, lng: e.target.value })}
          {...(problem ? { error: t(problem) } : { hint: t('evac.points.positionHint') })}
        />
      </div>
      <div className="flex justify-end gap-2">
        <Button onClick={onCancel}>{t('evac.points.cancel')}</Button>
        <Button type="submit" variant="primary" disabled={pending || d.name.trim() === '' || problem !== null}>
          {t('evac.points.save')}
        </Button>
      </div>
    </form>
  );
}

const toBody = (d: Draft) => ({
  name: d.name.trim(),
  directions: d.directions.trim(),
  lat: d.lat.trim() === '' ? null : Number(d.lat),
  lng: d.lng.trim() === '' ? null : Number(d.lng),
});

/** The muster points of one site: add, edit, stop using, and re-order. */
export function MusterPoints({ siteId, canEdit }: { siteId: string; canEdit: boolean }) {
  const qc = useQueryClient();
  const q = usePoints(siteId);
  const [editing, setEditing] = useState<string | 'new' | null>(null);
  const refresh = () => qc.invalidateQueries({ queryKey: evacKey });

  const create = useMutation({
    mutationFn: (d: Draft) => api.post('/evacuation/points', { body: { siteId, ...toBody(d) }, schema: EvacuationPoint }),
    onSuccess: async () => {
      setEditing(null);
      await refresh();
    },
  });
  const update = useMutation({
    mutationFn: (v: { id: string; patch: Record<string, unknown> }) => api.patch(`/evacuation/points/${v.id}`, { body: v.patch, schema: EvacuationPoint }),
    onSuccess: async () => {
      setEditing(null);
      await refresh();
    },
  });
  const order = useMutation({
    mutationFn: (ids: string[]) => api.put('/evacuation/points/order', { body: { siteId, pointIds: ids } }),
    onSuccess: refresh,
  });

  if (q.isError) return <ErrorPanel error={q.error} onRetry={() => void q.refetch()} />;
  if (!q.data) return <Spinner />;
  const points = [...q.data].sort((a, b) => a.order - b.order);

  const reorder = (next: EvacuationPoint[]) => {
    qc.setQueryData(pointsKey(siteId), next.map((p, i) => ({ ...p, order: i })));
    order.mutate(next.map((p) => p.id));
  };

  return (
    <Card
      title={t('evac.points.title')}
      footer={
        canEdit && editing === null ? (
          <Button variant="primary" onClick={() => setEditing('new')}>
            {t('evac.points.add')}
          </Button>
        ) : null
      }
    >
      <div className="space-y-3">
        <ErrorLine error={order.error ?? update.error} />
        {points.length === 0 && editing !== 'new' ? <EmptyState message={t('evac.points.empty')} /> : null}
        {points.length > 0 ? (
          <SortableList
            items={points}
            label={t('evac.points.list')}
            disabled={!canEdit}
            getKey={(p) => p.id}
            getLabel={(p) => p.name}
            onChange={reorder}
            renderItem={(p) =>
              editing === p.id ? (
                <PointForm
                  initial={{ name: p.name, directions: p.directions, lat: p.lat === null ? '' : String(p.lat), lng: p.lng === null ? '' : String(p.lng) }}
                  pending={update.isPending}
                  error={update.error}
                  onCancel={() => setEditing(null)}
                  onSave={(d) => update.mutate({ id: p.id, patch: toBody(d) })}
                />
              ) : (
                <div className="flex flex-wrap items-center gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="flex flex-wrap items-center gap-2 text-sm font-semibold">
                      {p.name}
                      {!p.active ? <Badge>{t('evac.points.inactive')}</Badge> : null}
                    </p>
                    {p.directions ? <p className="text-xs text-slate-600">{p.directions}</p> : null}
                  </div>
                  {canEdit ? (
                    <div className="flex gap-1">
                      <Button aria-label={t('evac.points.edit', { name: p.name })} onClick={() => setEditing(p.id)}>
                        <Pencil aria-hidden="true" className="h-4 w-4" />
                      </Button>
                      <Button
                        aria-label={p.active ? t('evac.points.deactivate', { name: p.name }) : t('evac.points.reactivate', { name: p.name })}
                        onClick={() => update.mutate({ id: p.id, patch: { active: !p.active } })}
                      >
                        <Power aria-hidden="true" className="h-4 w-4" />
                      </Button>
                    </div>
                  ) : null}
                </div>
              )
            }
          />
        ) : null}
        {editing === 'new' ? <PointForm initial={BLANK} pending={create.isPending} error={create.error} onCancel={() => setEditing(null)} onSave={(d) => create.mutate(d)} /> : null}
      </div>
    </Card>
  );
}
